import { useState, useEffect, useRef, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { 
  MessageCircle, 
  Send, 
  User, 
  Bot, 
  Clock, 
  AlertCircle,
  CheckCircle,
  XCircle,
  RefreshCw,
  ArrowLeft,
  ChevronLeft,
  Wifi,
  WifiOff
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import NotificationBell from '../components/NotificationBell';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const WS_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`.replace(/^http/, 'ws');

export default function ChatDashboard() {
  const navigate = useNavigate();
  const [sessions, setSessions] = useState([]);
  const [selectedSession, setSelectedSession] = useState(null);
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [stats, setStats] = useState(null);
  const [filter, setFilter] = useState('all');
  const [mobileView, setMobileView] = useState('list');
  const [wsConnected, setWsConnected] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const wsRef = useRef(null);
  const reconnectTimer = useRef(null);
  const selectedSessionRef = useRef(null);

  const token = localStorage.getItem('token');
  const headers = { 
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${token}` 
  };

  // Keep selectedSessionRef in sync
  useEffect(() => {
    selectedSessionRef.current = selectedSession;
  }, [selectedSession]);

  // ── WebSocket connection ──
  const connectWebSocket = useCallback(() => {
    if (!token) return;
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) return;

    const ws = new WebSocket(`${WS_BASE}/chat/ws/admin?token=${token}`);
    wsRef.current = ws;

    ws.onopen = () => {
      setWsConnected(true);
      if (reconnectTimer.current) {
        clearTimeout(reconnectTimer.current);
        reconnectTimer.current = null;
      }
    };

    ws.onmessage = (evt) => {
      try {
        const event = JSON.parse(evt.data);
        handleWsEvent(event);
      } catch {}
    };

    ws.onclose = () => {
      setWsConnected(false);
      wsRef.current = null;
      // Reconnect after 3s
      reconnectTimer.current = setTimeout(connectWebSocket, 3000);
    };

    ws.onerror = () => {
      ws.close();
    };

    // Ping every 25s to keep alive
    const pingInterval = setInterval(() => {
      if (ws.readyState === WebSocket.OPEN) {
        ws.send('ping');
      } else {
        clearInterval(pingInterval);
      }
    }, 25000);

    // Store cleanup
    ws._pingInterval = pingInterval;
  }, [token]);

  const handleWsEvent = useCallback((event) => {
    switch (event.type) {
      case 'new_message': {
        const { session_id, message } = event;
        // Update messages if this session is selected
        const current = selectedSessionRef.current;
        if (current && current.session_id === session_id) {
          setMessages(prev => {
            // Avoid duplicates
            if (prev.some(m => m.timestamp === message.timestamp && m.content === message.content)) return prev;
            return [...prev, message];
          });
        }
        // Update sessions list (move this session to top, increment unread)
        setSessions(prev => {
          const idx = prev.findIndex(s => s.session_id === session_id);
          if (idx === -1) {
            // New session appeared — reload sessions
            loadSessions();
            return prev;
          }
          const updated = [...prev];
          const s = { ...updated[idx], updated_at: message.timestamp };
          if (message.role === 'user' && (!current || current.session_id !== session_id)) {
            s.unread_count = (s.unread_count || 0) + 1;
          }
          updated.splice(idx, 1);
          updated.unshift(s);
          return updated;
        });
        break;
      }
      case 'session_update': {
        const { session_id, status } = event;
        setSessions(prev => prev.map(s =>
          s.session_id === session_id ? { ...s, status } : s
        ));
        const current = selectedSessionRef.current;
        if (current && current.session_id === session_id) {
          setSelectedSession(prev => prev ? { ...prev, status } : prev);
        }
        break;
      }
      case 'stats_update': {
        setStats(event.stats);
        break;
      }
      default:
        break;
    }
  }, []);

  // ── HTTP fetches (initial load + manual refresh) ──
  const loadStats = useCallback(async () => {
    try {
      const response = await fetch(`${API}/chat/admin/stats`, { headers });
      if (response.ok) setStats(await response.json());
    } catch {}
  }, []);

  const loadSessions = useCallback(async () => {
    try {
      const url = filter === 'all'
        ? `${API}/chat/admin/sessions`
        : `${API}/chat/admin/sessions?status=${filter}`;
      const response = await fetch(url, { headers });
      if (response.ok) {
        const data = await response.json();
        setSessions(data.sessions);
      }
    } catch {}
    setLoading(false);
  }, [filter]);

  const loadSessionMessages = useCallback(async (sessionId) => {
    try {
      const response = await fetch(`${API}/chat/admin/session/${sessionId}`, { headers });
      if (response.ok) {
        const data = await response.json();
        setMessages(data.messages);
        setSelectedSession(data.session);
      }
    } catch {}
  }, []);

  // ── Lifecycle ──
  useEffect(() => {
    loadStats();
    loadSessions();
    connectWebSocket();
    return () => {
      if (wsRef.current) {
        clearInterval(wsRef.current._pingInterval);
        wsRef.current.close();
      }
      if (reconnectTimer.current) clearTimeout(reconnectTimer.current);
    };
  }, [connectWebSocket, loadStats, loadSessions]);

  // Reload sessions when filter changes
  useEffect(() => { loadSessions(); }, [filter, loadSessions]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // ── Actions ──
  const selectSession = (session) => {
    setSelectedSession(session);
    loadSessionMessages(session.session_id);
    setMobileView('chat');
  };

  const backToList = () => setMobileView('list');

  const sendReply = async () => {
    if (!inputValue.trim() || !selectedSession || sending) return;
    setSending(true);
    try {
      const response = await fetch(`${API}/chat/admin/reply`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ session_id: selectedSession.session_id, content: inputValue.trim() })
      });
      if (response.ok) {
        // Message will arrive via WebSocket; also add locally for instant feedback
        const data = await response.json();
        setMessages(prev => {
          if (prev.some(m => m.timestamp === data.message.timestamp && m.content === data.message.content)) return prev;
          return [...prev, data.message];
        });
        setInputValue('');
      } else {
        toast.error('Failed to send reply');
      }
    } catch {
      toast.error('Failed to send reply');
    } finally {
      setSending(false);
    }
  };

  const closeSession = async (sessionId) => {
    try {
      const response = await fetch(`${API}/chat/close/${sessionId}`, { method: 'POST', headers });
      if (response.ok) {
        toast.success('Chat closed');
        // WebSocket will push the update; also refresh locally
        loadSessions();
        if (selectedSession?.session_id === sessionId) loadSessionMessages(sessionId);
      }
    } catch {}
  };

  const handleRefresh = () => {
    loadSessions();
    loadStats();
    if (!wsConnected) connectWebSocket();
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendReply(); }
  };

  const formatTime = (timestamp) => {
    if (!timestamp) return '';
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return `${diffHours}h ago`;
    return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  };

  const formatTimeFull = (timestamp) => {
    if (!timestamp) return '';
    return new Date(timestamp).toLocaleString();
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'active':
        return <Badge className="bg-blue-100 text-blue-700 text-[10px] px-1.5 py-0">Active</Badge>;
      case 'escalated':
        return <Badge className="bg-yellow-100 text-yellow-700 text-[10px] px-1.5 py-0">Escalated</Badge>;
      case 'closed':
        return <Badge className="bg-gray-100 text-gray-700 text-[10px] px-1.5 py-0">Closed</Badge>;
      default:
        return null;
    }
  };

  // ── Sessions list panel ──
  const SessionsPanel = () => (
    <div className="flex flex-col h-full" data-testid="sessions-panel">
      <div className="px-4 py-3 border-b border-gray-200 shrink-0">
        <div className="flex items-center justify-between mb-2">
          <h2 className="font-semibold text-gray-900 text-sm" data-testid="sessions-title">Chat Sessions</h2>
          {stats?.unread_messages > 0 && (
            <Badge className="bg-red-500 text-white text-[10px]" data-testid="unread-badge">{stats.unread_messages} unread</Badge>
          )}
        </div>
        <div className="flex gap-1.5" data-testid="filter-buttons">
          {['all', 'escalated', 'active', 'closed'].map((f) => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={`text-xs px-2.5 py-1 rounded-full transition-colors ${
                filter === f ? 'bg-[#007a33] text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
              data-testid={`filter-${f}`}
            >
              {f.charAt(0).toUpperCase() + f.slice(1)}
            </button>
          ))}
        </div>
      </div>
      <div className="flex-1 overflow-y-auto" data-testid="sessions-list">
        {loading ? (
          <p className="text-center text-gray-500 py-8 text-sm">Loading...</p>
        ) : sessions.length === 0 ? (
          <div className="text-center text-gray-400 py-12">
            <MessageCircle className="w-8 h-8 mx-auto mb-2 opacity-40" />
            <p className="text-sm">No chat sessions</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-100">
            {sessions.map((session) => (
              <div
                key={session.session_id}
                onClick={() => selectSession(session)}
                className={`px-4 py-3 cursor-pointer transition-colors active:bg-gray-100 ${
                  selectedSession?.session_id === session.session_id
                    ? 'bg-[#007a33]/5 border-l-2 border-l-[#007a33]'
                    : 'hover:bg-gray-50'
                }`}
                data-testid={`session-item-${session.session_id}`}
              >
                <div className="flex items-center justify-between mb-0.5">
                  <span className="font-medium text-sm text-gray-900 truncate mr-2">
                    {session.user_name || session.user_email || 'Visitor'}
                  </span>
                  <div className="flex items-center gap-1.5 shrink-0">
                    {session.unread_count > 0 && (
                      <span className="w-5 h-5 rounded-full bg-red-500 text-white text-[10px] flex items-center justify-center font-medium">
                        {session.unread_count}
                      </span>
                    )}
                    {getStatusBadge(session.status)}
                  </div>
                </div>
                <p className="text-xs text-gray-400 truncate">{session.session_id}</p>
                <p className="text-xs text-gray-400 mt-0.5">{formatTime(session.updated_at)}</p>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );

  // ── Chat view panel ──
  const ChatPanel = () => (
    <div className="flex flex-col h-full" data-testid="chat-panel">
      {selectedSession ? (
        <>
          <div className="px-3 py-2.5 border-b border-gray-200 shrink-0" data-testid="chat-header">
            <div className="flex items-center gap-2">
              <button
                onClick={backToList}
                className="lg:hidden p-1.5 -ml-1 rounded-lg hover:bg-gray-100 transition-colors"
                data-testid="chat-back-btn"
              >
                <ChevronLeft className="w-5 h-5 text-gray-600" />
              </button>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <h3 className="font-semibold text-sm text-gray-900 truncate">
                    {selectedSession.user_name || selectedSession.user_email || 'Visitor'}
                  </h3>
                  {getStatusBadge(selectedSession.status)}
                </div>
                <p className="text-xs text-gray-400 truncate">
                  {selectedSession.user_email || 'No email provided'}
                </p>
              </div>
              {selectedSession.status !== 'closed' && (
                <Button 
                  size="sm" variant="outline"
                  onClick={() => closeSession(selectedSession.session_id)}
                  className="shrink-0 h-8 text-xs"
                  data-testid="close-chat-btn"
                >
                  <XCircle className="w-3.5 h-3.5 sm:mr-1" />
                  <span className="hidden sm:inline">Close</span>
                </Button>
              )}
            </div>
          </div>

          <div className="flex-1 overflow-y-auto px-3 py-3 space-y-2.5" data-testid="messages-area">
            {messages.map((msg, index) => (
              <div
                key={index}
                className={`flex ${msg.role === 'user' ? 'justify-start' : 'justify-end'}`}
              >
                <div
                  className={`max-w-[85%] sm:max-w-[70%] rounded-2xl px-3.5 py-2 ${
                    msg.role === 'user'
                      ? 'bg-gray-100 text-gray-800 rounded-bl-md'
                      : msg.role === 'system'
                      ? 'bg-yellow-50 text-yellow-800 text-xs italic border border-yellow-200'
                      : msg.role === 'agent'
                      ? 'bg-[#007a33] text-white rounded-br-md'
                      : 'bg-blue-50 text-blue-800 border border-blue-200 rounded-br-md'
                  }`}
                >
                  <div className="flex items-center gap-1 mb-0.5 text-[10px] opacity-70">
                    {msg.role === 'user' ? <User className="w-2.5 h-2.5" /> : msg.role === 'agent' ? <User className="w-2.5 h-2.5" /> : <Bot className="w-2.5 h-2.5" />}
                    <span>{msg.sender_name || msg.role}</span>
                  </div>
                  <p className="text-sm leading-relaxed whitespace-pre-wrap">{msg.content}</p>
                  <p className="text-[10px] opacity-50 mt-1">{formatTimeFull(msg.timestamp)}</p>
                </div>
              </div>
            ))}
            <div ref={messagesEndRef} />
          </div>

          {selectedSession.status !== 'closed' && (
            <div className="border-t border-gray-200 px-3 py-2.5 shrink-0" data-testid="reply-area">
              <div className="flex gap-2">
                <Input
                  ref={inputRef}
                  placeholder="Type your reply..."
                  value={inputValue}
                  onChange={(e) => setInputValue(e.target.value)}
                  onKeyPress={handleKeyPress}
                  disabled={sending}
                  className="text-sm h-10"
                  data-testid="reply-input"
                />
                <Button 
                  onClick={sendReply} 
                  disabled={!inputValue.trim() || sending}
                  className="bg-[#007a33] hover:bg-[#005c26] h-10 w-10 p-0 shrink-0"
                  data-testid="send-btn"
                >
                  <Send className="w-4 h-4" />
                </Button>
              </div>
            </div>
          )}
        </>
      ) : (
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center text-gray-400 px-4">
            <MessageCircle className="w-10 h-10 mx-auto mb-3 opacity-40" />
            <p className="text-sm">Select a chat session to view messages</p>
          </div>
        </div>
      )}
    </div>
  );

  return (
    <div className="h-[100dvh] flex flex-col bg-[#F8FAFC]" data-testid="chat-dashboard">
      {/* Top bar */}
      <div className="shrink-0 bg-white border-b border-gray-200 px-3 sm:px-6 py-2.5 sm:py-3" data-testid="chat-topbar">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 sm:gap-3 min-w-0">
            <Button variant="ghost" size="sm" onClick={() => navigate('/super-admin')} className="shrink-0 px-2" data-testid="back-to-admin">
              <ArrowLeft className="w-4 h-4" />
            </Button>
            <h1 className="text-base sm:text-xl font-bold text-gray-900 truncate" data-testid="page-title">Live Chat</h1>
            {wsConnected ? (
              <Wifi className="w-3.5 h-3.5 text-emerald-500 shrink-0" data-testid="ws-connected" />
            ) : (
              <WifiOff className="w-3.5 h-3.5 text-gray-400 shrink-0" data-testid="ws-disconnected" />
            )}
          </div>
          <div className="flex items-center gap-1 sm:gap-2">
            <NotificationBell />
            <Button onClick={handleRefresh} variant="outline" size="sm" className="shrink-0" data-testid="refresh-btn">
              <RefreshCw className="w-4 h-4 sm:mr-1.5" />
              <span className="hidden sm:inline text-sm">Refresh</span>
            </Button>
          </div>
        </div>
      </div>

      {/* Stats bar */}
      {stats && (
        <div className="shrink-0 bg-white border-b border-gray-200 px-3 sm:px-6 py-2" data-testid="stats-bar">
          <div className="flex items-center gap-3 sm:gap-6 overflow-x-auto scrollbar-hide">
            <div className="flex items-center gap-1.5 shrink-0" data-testid="stat-total">
              <MessageCircle className="w-4 h-4 text-blue-500" />
              <span className="text-sm font-semibold">{stats.total_sessions}</span>
              <span className="text-xs text-gray-400">Total</span>
            </div>
            <div className="flex items-center gap-1.5 shrink-0" data-testid="stat-escalated">
              <AlertCircle className="w-4 h-4 text-yellow-500" />
              <span className="text-sm font-semibold">{stats.escalated}</span>
              <span className="text-xs text-gray-400">Escalated</span>
            </div>
            <div className="flex items-center gap-1.5 shrink-0" data-testid="stat-active">
              <Clock className="w-4 h-4 text-green-500" />
              <span className="text-sm font-semibold">{stats.active}</span>
              <span className="text-xs text-gray-400">Active</span>
            </div>
            <div className="flex items-center gap-1.5 shrink-0" data-testid="stat-closed">
              <CheckCircle className="w-4 h-4 text-gray-400" />
              <span className="text-sm font-semibold">{stats.closed}</span>
              <span className="text-xs text-gray-400">Closed</span>
            </div>
          </div>
        </div>
      )}

      {/* Main content */}
      <div className="flex-1 min-h-0">
        <div className="hidden lg:grid lg:grid-cols-[320px_1fr] h-full">
          <div className="border-r border-gray-200 bg-white">
            <SessionsPanel />
          </div>
          <div className="bg-white">
            <ChatPanel />
          </div>
        </div>
        <div className="lg:hidden h-full bg-white">
          {mobileView === 'list' ? <SessionsPanel /> : <ChatPanel />}
        </div>
      </div>
    </div>
  );
}
